import React, { useState, useEffect } from "react";

import { useHistory } from "react-router-dom";

import { API, graphqlOperation } from "aws-amplify";
import { countOutboundShipments } from "../../../graphql/outbound/custom-queries";

import { addMonths, format, startOfMonth, sub } from "date-fns";

import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { Typography } from "@material-ui/core";

import { useNotify } from "ra-core";

import Title from "../Title";
import { logError } from "../../../utils/appUtils";
import { OutboundStatus } from "../../../utils/constants";

const DEFAULT_MONTHS = 12;
const emptyData = Array.from({ length: DEFAULT_MONTHS }, () => ({}));

const CustomizedLabel = (props) => {
  const { x, y, fill, value } = props;

  return (
    <text
      x={x}
      y={y}
      dy={-4}
      fontSize="16"
      fontFamily="sans-serif"
      fill={fill}
      textAnchor="middle"
    >
      {value}
    </text>
  );
};

const MonthlyOutboundsChart = ({ reloadYardId, ...props }) => {
  const notify = useNotify();
  const history = useHistory();
  const [average, setAverage] = useState(0);
  const [data, setData] = useState(emptyData);

  useEffect(() => {
    getOutboundCarsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBarClick = (barData) => {
    const monthStart = barData?.payload?.monthStart;

    if (monthStart) {
      history.push({
        pathname: "/reportDailyOutbounds",
        search: `?monthStart=${encodeURIComponent(monthStart)}`,
      });
    } else {
      history.push("/reportDailyOutbounds");
    }
  };

  const getOutboundCarsData = async () => {
    // Get the last 12 months, including current month
    const startDate = startOfMonth(sub(new Date(), { months: 11 }));
    let tempData = [];

    try {
      const queryList = [];
      const yardId = reloadYardId ? reloadYardId : window.appUser?.reloadYardId;
      debugger;

      for (let i = 0; i <= 11; i++) {
        const newDate = addMonths(startDate, i);
        const nextDate = addMonths(newDate, 1);
        const monthName = format(newDate, "MMM");
        const options = { month: "2-digit", year: "numeric" };
        const barDate = newDate.toLocaleDateString("en-US", options);

        tempData[i] = {
          name: monthName,
          date: barDate,
          monthStart: newDate.toISOString(),
          amt: 0,
        };

        queryList[i] = API.graphql(
          graphqlOperation(countOutboundShipments, {
            filter: {
              reloadYardId: { eq: yardId },
              status: { eq: OutboundStatus.RELEASED },
              and: [
                { departureDateTime: { gte: newDate } },
                { departureDateTime: { lt: nextDate } },
              ],
            },
            limit: 1000,
          })
        );
      }

      const promiseResults = await Promise.all(queryList);
      let totalAmt = 0;
      let length = 0;

      promiseResults.forEach((result, idx) => {
        const amt = result.data.searchOutboundShipments.total;
        tempData[idx].amt = amt;
        totalAmt += amt;
        length += amt != null ? 1 : 0;
      });

      const avg = length > 0 ? Math.round(totalAmt / length) : 0;

      setData(tempData);
      setAverage(avg);
    } catch (err) {
      logError("OutboundCarsByMonth:getOutboundCarsData", err, notify);
    }
  };

  return (
    <>
      <Title>Monthly Outbounds</Title>
      <ResponsiveContainer>
        <BarChart
          data={data}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" tickSize dy="25" />
          <YAxis />
          <Tooltip
            content={({ payload }) => {
              if (payload && payload.length) {
                const { date, amt } = payload[0].payload;
                return (
                  <div
                    style={{
                      color: "black",
                      background: "white",
                      padding: "8px",
                      border: "1px solid black",
                    }}
                  >
                    <Typography>{`Date: ${date}`}</Typography>
                    <Typography>{`Value: ${amt}`}</Typography>
                    <Typography>{`Average: ${average}`}</Typography>
                  </div>
                );
              }
              return null;
            }}
          />
          {average && (
            <ReferenceLine
              y={average}
              stroke="red"
              strokeWidth={6}
              strokeDasharray="6 6"
            />
          )}
          <Bar
            dataKey="amt"
            stackId="amt"
            onClick={handleBarClick}
            cursor="pointer"
          >
            label={<CustomizedLabel />}
            {data.map((entry, index) => (
              <Cell fill={entry.amt > average ? "green" : "orange"} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </>
  );
};

export default MonthlyOutboundsChart;
